import { Asset, AssetDependency, AssetState, DependencyStatus, SortOrder } from '@/api/generatedApi';
import { DependencyFilter, DependencySortField } from '@/api/dependencyTypes';
import { useGetAssetDependencies } from '@/hooks/api/assets/useGetAssetDependencies';
import { useTenants } from '@/services/tenants/useTenants';
import {
  isSameSelectionPath,
  isSelectionPathAncestor,
  toSelectionPathSegment,
} from '@physna/core/domain/viewer/selection-path';
import { useResolveFlow } from './useResolveFlow';
import { getFilename } from '@/utils/path';
import {
  Box,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  SxProps,
  Tooltip,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { TreeItem } from '@mui/x-tree-view';
import { ErrorOutline, LinkOff, SyncAlt, UploadFile, ViewInAr } from '@mui/icons-material';
import { StyledPopover, StyledList, StyledListItem, StyledListHeader } from '@/components/AssetsPage/PopoverList';
import { AssetSelectionModal } from '@/components/modals/AssetSelectionModal';
import { FileInputs } from '@/components/Upload/FileInputs/FileInputs';
import { UploadDrawer } from '@/components/Upload/UploadDrawer/UploadDrawer';
import { useDependencyUpload } from '@/hooks/useDependencyUpload';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { MissingDependencyIcon } from '@/components/MissingDependencyIcon/MissingDependencyIcon';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { ConfirmDialog } from './ConfirmDialog';

const REVEAL_DELAY_MS = 100;
const EMPTY_SELECTION_PATH: string[] = [];

const useStyles = createUseStyles(() => ({
  DependencyTreeView_LoadState: {
    marginLeft: '15px',
    height: '32px',
  },
  DependencyTreeView_LoadError: {
    marginLeft: '15px',
    minHeight: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  DependencyTreeView_TreeView: {
    paddingLeft: '.5rem',
  },
  TreeItemWrapper_label: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '2px 0',
    height: '1.5rem',
  },
  TreeItemWrapper_text: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    fontSize: '0.875rem',
  },
  TreeItemWrapper_resolveButton: {
    padding: '4px',
    marginLeft: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  },
  TreeItemWrapper_filename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    maxWidth: 'calc(100% - 20px)',
  },
  TreeItemWrapper_missingIcon: {
    marginRight: '6px',
    display: 'flex',
  },
}));

interface LabelWrapperProperties {
  children: React.ReactElement;
  isMissing?: boolean;
  isMissingTooltip?: ReactNode;
}

const LabelWrapper = ({ children, isMissing, isMissingTooltip }: LabelWrapperProperties) => {
  return isMissing ? <Tooltip title={isMissingTooltip}>{children}</Tooltip> : children;
};

interface TreeItemWrapperProperties {
  id: string;
  path: string;
  displayName?: string;
  children?: ReactNode;
  sx?: SxProps;
  isMissing?: boolean;
  isResolved?: boolean;
  isHighlighted?: boolean;
  onClick?: () => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  assemblyId?: string;
  isUploading?: boolean;
  isIndexing?: boolean;
  hasMissingChildDependencies?: boolean;
}

export const TreeItemWrapper = ({
  id,
  path,
  displayName,
  sx,
  children,
  isMissing,
  isResolved,
  isHighlighted = false,
  onClick,
  onResolveClick,
  onSwitchClick,
  assemblyId,
  isUploading = false,
  isIndexing = false,
  hasMissingChildDependencies = false,
}: TreeItemWrapperProperties) => {
  const c = useStyles();
  const shouldShowResolveButton = isMissing && onResolveClick;
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | undefined>();
  const itemReference = useRef<HTMLLIElement>(null);

  const hasChildren = !!children && (!Array.isArray(children) || children.length > 0);

  useEffect(() => {
    if (!isHighlighted || !itemReference.current) return;
    const timer = setTimeout(() => {
      itemReference.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, REVEAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isHighlighted]);

  const handleContextMenu = (event: React.MouseEvent) => {
    if (!isResolved || !onSwitchClick) return;
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  return (
    <>
      <TreeItem
        ref={itemReference}
        itemId={id}
        sx={{
          ...sx,
          ...(isHighlighted
            ? {
                '& > .MuiTreeItem-content': {
                  backgroundColor: 'action.selected',
                },
              }
            : {}),
        }}
        {...(!hasChildren && isMissing ? { slots: { icon: MissingDependencyIcon } } : {})}
        label={
          <LabelWrapper isMissing={isMissing} isMissingTooltip={getFilename(path)}>
            <Box
              className={c.TreeItemWrapper_label}
              onClick={event => {
                event.stopPropagation();
                onClick?.();
              }}
              onContextMenu={handleContextMenu}
              gap={1}
              style={{ cursor: onClick ? 'pointer' : undefined }}
            >
              <Box className={c.TreeItemWrapper_text}>
                {isMissing && hasChildren && (
                  <span className={c.TreeItemWrapper_missingIcon} data-testid='MissingDependenciesIcon'>
                    <MissingDependencyIcon hideTooltip />
                  </span>
                )}
                {isMissing ? (
                  <button
                    type='button'
                    className={c.TreeItemWrapper_filename}
                    onClick={event => {
                      event.stopPropagation();
                      void navigator.clipboard.writeText(getFilename(path));
                    }}
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    {getFilename(path)}
                  </button>
                ) : (
                  <Tooltip title={displayName ?? getFilename(path)}>
                    <span className={c.TreeItemWrapper_filename}>{displayName ?? getFilename(path)}</span>
                  </Tooltip>
                )}
              </Box>
              {isIndexing && (
                <LinearProgress
                  sx={{ width: '28px', ml: 1, mr: '2px', flexShrink: 0 }}
                  data-testid='dependency-indexing-progress'
                />
              )}
              {hasMissingChildDependencies && (
                <Tooltip title='Contains missing dependencies'>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '26px',
                      height: '26px',
                      flexShrink: 0,
                    }}
                  >
                    <ErrorOutline
                      data-testid='MissingAssetIcon'
                      sx={{ color: 'warning.dark', fontSize: '12px', cursor: 'default' }}
                    />
                  </Box>
                </Tooltip>
              )}
              {shouldShowResolveButton &&
                (isUploading ? (
                  <IconButton size='small' disabled>
                    <CircularProgress size={16} />
                  </IconButton>
                ) : (
                  <IconButton
                    data-testid='resolve-dependency-button'
                    className={c.TreeItemWrapper_resolveButton}
                    onClick={event => {
                      event.stopPropagation();
                      onResolveClick(event, path, assemblyId ?? '');
                    }}
                    size='small'
                    color='error'
                    sx={{ '& .MuiSvgIcon-root': { color: 'error.dark' } }}
                    aria-label={`Resolve missing dependency ${getFilename(path)}`}
                  >
                    <LinkOff sx={{ fontSize: '16px' }} />
                  </IconButton>
                ))}
            </Box>
          </LabelWrapper>
        }
      >
        {children}
      </TreeItem>
      {isResolved && onSwitchClick && (
        <Menu
          open={contextMenu !== undefined}
          onClose={() => setContextMenu(undefined)}
          anchorReference='anchorPosition'
          anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        >
          <MenuItem
            data-testid='switch-dependency-button'
            onClick={event => {
              setContextMenu(undefined);
              onSwitchClick(
                event as unknown as React.MouseEvent<HTMLButtonElement>,
                path,
                assemblyId ?? '',
                displayName
              );
            }}
          >
            <ListItemIcon>
              <SyncAlt sx={{ fontSize: '16px' }} />
            </ListItemIcon>
            <ListItemText>Switch resolved asset</ListItemText>
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

interface RevealPagination {
  dependencies?: AssetDependency[];
  ancestorSegments: string[];
  selectedSelectionPath?: string[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void | Promise<unknown>;
}

function useRevealPagination({
  dependencies,
  ancestorSegments,
  selectedSelectionPath,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: RevealPagination): void {
  const isOnRevealPath = ancestorSegments.every((segment, index) => selectedSelectionPath?.[index] === segment);
  const nextSegment = isOnRevealPath ? selectedSelectionPath?.[ancestorSegments.length] : undefined;
  const isSegmentLoaded = dependencies?.some(
    dependency => dependency.asset?.id && toSelectionPathSegment(dependency.asset.id, dependency.path) === nextSegment
  );

  useEffect(() => {
    if (!nextSegment || isSegmentLoaded) return;
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [nextSegment, isSegmentLoaded, hasNextPage, isFetchingNextPage, fetchNextPage]);
}

const DependencyLoadError = ({ onRetry }: { onRetry: () => unknown }) => {
  const c = useStyles();

  return (
    <Box role='status' className={c.DependencyTreeView_LoadError}>
      <ErrorOutline sx={{ color: 'warning.dark', fontSize: '12px' }} />
      <Typography variant='body2' color='text.secondary'>
        {"Couldn't load parts ·"}
      </Typography>
      <Link
        component='button'
        variant='body2'
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          void onRetry();
        }}
      >
        Retry
      </Link>
    </Box>
  );
};

const getUniqueItemId = (item: AssetDependency, parentPath: string = ''): string => {
  const baseId = item.asset?.id ? `${item.asset.id}-${item.path}` : `missing-${item.path}`;
  return parentPath ? `${parentPath}/${baseId}` : baseId;
};

const toDependencyContext = (
  dependency: AssetDependency,
  assemblyId: string,
  assemblyPath: string
): DependencyContext => ({
  dependencyPath: dependency.path,
  assemblyId,
  assemblyName: getFilename(assemblyPath),
  assetPath: dependency.asset?.path,
});

interface DependencyListProperties {
  dependencies: AssetDependency[];
  parentItemId: string;
  level: number;
  assemblyId: string;
  assemblyPath: string;
  onTreeItemClick?: (itemId: string, context?: DependencyContext, selectionPath?: string[]) => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  uploadingPaths: Set<string>;
  ancestorSegments: string[];
  selectedSelectionPath?: string[];
  onRevealAncestor?: (itemId: string) => void;
  expandedItems: ReadonlySet<string>;
  filter?: DependencyFilter;
  sortBy?: DependencySortField;
  sortOrder?: SortOrder;
}

const DependencyList = ({
  dependencies,
  parentItemId,
  level,
  assemblyId,
  assemblyPath,
  onTreeItemClick,
  onResolveClick,
  onSwitchClick,
  uploadingPaths,
  ancestorSegments,
  selectedSelectionPath,
  onRevealAncestor,
  expandedItems,
  filter,
  sortBy,
  sortOrder,
}: DependencyListProperties) =>
  dependencies.map(dependency => {
    const itemId = parentItemId ? `${parentItemId}/${getUniqueItemId(dependency)}` : getUniqueItemId(dependency);
    const selectionPath = dependency.asset?.id
      ? [...ancestorSegments, toSelectionPathSegment(dependency.asset.id, dependency.path)]
      : ancestorSegments;
    const context = toDependencyContext(dependency, assemblyId, assemblyPath);
    return dependency.hasDependencies && dependency.asset ? (
      <DependencyTreeItem
        key={itemId}
        item={dependency}
        parentPath={parentItemId}
        level={level}
        assemblyId={assemblyId}
        assemblyPath={assemblyPath}
        onTreeItemClick={onTreeItemClick}
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        uploadingPaths={uploadingPaths}
        ancestorSegments={ancestorSegments}
        selectedSelectionPath={selectedSelectionPath}
        onRevealAncestor={onRevealAncestor}
        expandedItems={expandedItems}
        filter={filter}
        sortBy={sortBy}
        sortOrder={sortOrder}
      />
    ) : (
      <TreeItemWrapper
        key={itemId}
        id={itemId}
        path={dependency.path}
        displayName={dependency.asset ? getFilename(dependency.asset.path) : undefined}
        assemblyId={assemblyId}
        onClick={onTreeItemClick && (() => onTreeItemClick(dependency.asset?.id ?? '', context, selectionPath))}
        isMissing={!dependency.asset}
        isResolved={dependency.status === DependencyStatus.Resolved}
        isHighlighted={isSameSelectionPath(selectionPath, selectedSelectionPath)}
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        isUploading={uploadingPaths.has(dependency.path)}
        isIndexing={dependency.asset?.state === AssetState.Indexing}
        hasMissingChildDependencies={dependency.asset?.isMissingDependencies ?? false}
      />
    );
  });

interface DependencyTreeItemProperties {
  item: AssetDependency;
  parentPath?: string;
  level?: number;
  assemblyId: string;
  assemblyPath: string;
  onTreeItemClick?: (itemId: string, context?: DependencyContext, selectionPath?: string[]) => void;
  onResolveClick?: (event: React.MouseEvent<HTMLButtonElement>, path: string, assemblyId: string) => void;
  onSwitchClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
    path: string,
    assemblyId: string,
    currentResolvedName?: string
  ) => void;
  uploadingPaths?: Set<string>;
  ancestorSegments: string[];
  selectedSelectionPath?: string[];
  onRevealAncestor?: (itemId: string) => void;
  expandedItems: ReadonlySet<string>;
  filter?: DependencyFilter;
  sortBy?: DependencySortField;
  sortOrder?: SortOrder;
}

const DependencyTreeItem = ({
  item,
  parentPath,
  level = 0,
  assemblyId,
  assemblyPath,
  onTreeItemClick,
  onResolveClick,
  onSwitchClick,
  uploadingPaths = new Set(),
  ancestorSegments,
  selectedSelectionPath,
  onRevealAncestor,
  expandedItems,
  filter,
  sortBy,
  sortOrder,
}: DependencyTreeItemProperties) => {
  const c = useStyles();
  const itemReference = useRef<HTMLDivElement>(null);
  const currentItemId = getUniqueItemId(item, parentPath);
  const selectionPath = item.asset?.id
    ? [...ancestorSegments, toSelectionPathSegment(item.asset.id, item.path)]
    : ancestorSegments;
  const isRevealAncestor = isSelectionPathAncestor(selectionPath, selectedSelectionPath);

  useEffect(() => {
    if (isRevealAncestor) onRevealAncestor?.(currentItemId);
  }, [isRevealAncestor, currentItemId, onRevealAncestor]);
  const { dependencies, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAssetDependencies({
      assetId: item.asset?.id,
      assetTenantId: item.asset?.tenantId,
      enabled: item.hasDependencies && expandedItems.has(currentItemId),
      filter,
      sortBy,
      sortOrder,
    });

  useInfiniteScroll(itemReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useRevealPagination({
    dependencies,
    ancestorSegments: selectionPath,
    selectedSelectionPath,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const baseLevel = level + 1;
  const hasMissingChildDependencies =
    (item.asset?.isMissingDependencies ?? false) ||
    (dependencies?.some(dep => !dep.asset || dep.asset?.isMissingDependencies) ?? false);

  return (
    <div ref={itemReference}>
      <TreeItemWrapper
        id={currentItemId}
        path={item.path}
        displayName={item.asset ? getFilename(item.asset.path) : undefined}
        assemblyId={assemblyId}
        onClick={
          onTreeItemClick &&
          (() =>
            onTreeItemClick(item.asset?.id ?? '', toDependencyContext(item, assemblyId, assemblyPath), selectionPath))
        }
        isMissing={!item.asset}
        isResolved={item.status === DependencyStatus.Resolved}
        isHighlighted={isSameSelectionPath(selectionPath, selectedSelectionPath)}
        onResolveClick={onResolveClick}
        onSwitchClick={onSwitchClick}
        isUploading={uploadingPaths.has(item.path)}
        isIndexing={item.asset?.state === AssetState.Indexing}
        hasMissingChildDependencies={hasMissingChildDependencies}
      >
        {dependencies && (
          <DependencyList
            dependencies={dependencies}
            parentItemId={currentItemId}
            level={baseLevel}
            assemblyId={item.asset?.id ?? ''}
            assemblyPath={item.asset?.path ?? item.path}
            onTreeItemClick={onTreeItemClick}
            onResolveClick={onResolveClick}
            onSwitchClick={onSwitchClick}
            uploadingPaths={uploadingPaths}
            ancestorSegments={selectionPath}
            selectedSelectionPath={selectedSelectionPath}
            onRevealAncestor={onRevealAncestor}
            expandedItems={expandedItems}
            filter={filter}
            sortBy={sortBy}
            sortOrder={sortOrder}
          />
        )}
        {error && dependencies.length === 0 && <DependencyLoadError onRetry={refetch} />}
        {isLoading && (
          <Skeleton
            data-testid='dependency-tree-item-skeleton'
            width={180}
            className={c.DependencyTreeView_LoadState}
            variant='text'
          />
        )}
        {isFetchingNextPage && (
          <Box className={c.DependencyTreeView_LoadState}>
            <Skeleton width={180} variant='text' />
          </Box>
        )}
      </TreeItemWrapper>
    </div>
  );
};

export interface DependencyContext {
  dependencyPath: string;
  assemblyId: string;
  assemblyName: string;
  assetPath?: string;
}

export interface DependencyTreeViewProperties {
  asset: Asset;
  onTreeItemClick?: (itemId: string, context?: DependencyContext, selectionPath?: string[]) => void;
  selectedSelectionPath?: string[];
  onRevealAncestor?: (itemId: string) => void;
  filter?: DependencyFilter;
  sortBy?: DependencySortField;
  sortOrder?: SortOrder;
  readOnly?: boolean;
  expandedItems: ReadonlySet<string>;
}

export const DependencyTreeView = ({
  asset,
  onTreeItemClick,
  selectedSelectionPath,
  onRevealAncestor,
  expandedItems,
  filter,
  sortBy,
  sortOrder,
  readOnly = false,
}: DependencyTreeViewProperties) => {
  const { getTenantId } = useTenants();
  const isEditable = !readOnly && asset.tenantId === getTenantId();
  const treeViewReference = useRef<HTMLDivElement>(null);
  const [hasIndexingDeps, setHasIndexingDeps] = useState(false);
  const { dependencies, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetAssetDependencies({
      assetId: asset.id,
      assetTenantId: asset.tenantId,
      refetchInterval: hasIndexingDeps ? 15_000 : false,
      filter,
      sortBy,
      sortOrder,
    });

  useInfiniteScroll(treeViewReference, {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useRevealPagination({
    dependencies,
    ancestorSegments: EMPTY_SELECTION_PATH,
    selectedSelectionPath,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  useEffect(() => {
    setHasIndexingDeps(dependencies?.some(dep => dep.asset?.state === AssetState.Indexing) ?? false);
  }, [dependencies]);

  const {
    handleResolveClick,
    handleSwitchClick,
    handleExistingAssetClick,
    handleUploadAssetClick,
    handleAssetSelect,
    resolveByUpload,
    isResolvePopoverOpen,
    resolveAnchorElement,
    closeResolvePopover,
    isAssetSelectionOpen,
    setIsAssetSelectionOpen,
    isSwitchMode,
    pendingResolvePath,
    pendingAssemblyId,
    getConfirmDialogProps,
  } = useResolveFlow();

  const [resolveTargetAssemblyId, setResolveTargetAssemblyId] = useState<string | undefined>();

  const previousIsAssetSelectionOpen = useRef(false);
  useEffect(() => {
    if (isAssetSelectionOpen && !previousIsAssetSelectionOpen.current) {
      setResolveTargetAssemblyId(pendingAssemblyId.current || undefined);
    }
    if (!isAssetSelectionOpen) {
      setResolveTargetAssemblyId(undefined);
    }
    previousIsAssetSelectionOpen.current = isAssetSelectionOpen;
  }, [isAssetSelectionOpen, pendingAssemblyId]);

  const { excludedResolveTargetIds } = useGetAssetDependencies({
    assetId: resolveTargetAssemblyId,
    enabled: !!resolveTargetAssemblyId,
    includeExcludedResolveTargets: true,
  });

  const {
    fileInputReference,
    folderInputReference,
    files,
    uploadProgress,
    drawerOpen,
    handleDrawerClose,
    uploadingPaths,
    handleUploadClick,
    fileInputProps,
    pendingFile,
    pendingDependencyPath,
    confirmOpen,
    confirmUpload,
    cancelUpload,
  } = useDependencyUpload({
    onUploaded: resolveByUpload,
  });

  const c = useStyles();

  if (!dependencies) {
    return;
  }

  // Only when this level has nothing to show: react-query keeps the error until this same query
  // refetches, so a level holding rows would otherwise keep a warning about a refresh that failed.
  if (error && dependencies.length === 0) {
    return <DependencyLoadError onRetry={refetch} />;
  }

  return (
    <>
      <div ref={treeViewReference} data-testid='dependency-tree' className={c.DependencyTreeView_TreeView}>
        {isLoading && (
          <Skeleton
            data-testid='dependency-tree-skeleton'
            width={180}
            className={c.DependencyTreeView_LoadState}
            variant='text'
          />
        )}
        <DependencyList
          dependencies={dependencies}
          parentItemId=''
          level={0}
          assemblyId={asset.id}
          assemblyPath={asset.path}
          onTreeItemClick={onTreeItemClick}
          onResolveClick={isEditable ? handleResolveClick : undefined}
          onSwitchClick={isEditable ? handleSwitchClick : undefined}
          uploadingPaths={uploadingPaths}
          ancestorSegments={EMPTY_SELECTION_PATH}
          selectedSelectionPath={selectedSelectionPath}
          onRevealAncestor={onRevealAncestor}
          expandedItems={expandedItems}
          filter={filter}
          sortBy={sortBy}
          sortOrder={sortOrder}
        />
        {isFetchingNextPage && (
          <Box className={c.DependencyTreeView_LoadState}>
            <Skeleton width={180} variant='text' />
          </Box>
        )}
      </div>
      <StyledPopover
        open={isEditable && isResolvePopoverOpen}
        anchorEl={resolveAnchorElement}
        onClose={closeResolvePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <ListItemText>Resolve Dependency</ListItemText>
          </StyledListHeader>
          <Divider />
          <StyledListItem onClick={handleExistingAssetClick} data-testid='resolve-existing-asset'>
            <ListItemIcon>
              <ViewInAr />
            </ListItemIcon>
            <ListItemText>Existing Asset</ListItemText>
          </StyledListItem>
          <Divider />
          <StyledListItem onClick={() => handleUploadAssetClick(handleUploadClick)} data-testid='resolve-upload-asset'>
            <ListItemIcon>
              <UploadFile />
            </ListItemIcon>
            <ListItemText>Upload Asset</ListItemText>
          </StyledListItem>
        </StyledList>
      </StyledPopover>
      <AssetSelectionModal
        open={isEditable && isAssetSelectionOpen}
        onClose={() => setIsAssetSelectionOpen(false)}
        onAssetSelect={handleAssetSelect}
        title={isSwitchMode ? 'Switch Resolved Asset' : 'Resolve Dependency'}
        description={
          isSwitchMode
            ? `Select a different asset to resolve "${getFilename(pendingResolvePath)}".`
            : `Select an existing asset to resolve the missing dependency "${getFilename(pendingResolvePath)}".`
        }
        initialFolderId={asset.folderId}
        excludeAssetIds={excludedResolveTargetIds}
        excludeTooltip='This asset cannot be selected because it would create a circular dependency'
      />
      <ConfirmDialog
        {...(() => {
          const resolveProperties = getConfirmDialogProps(pendingDependencyPath, pendingFile);
          return {
            ...resolveProperties,
            open: isEditable && (resolveProperties.open || confirmOpen),
            onConfirm: resolveProperties.open ? resolveProperties.onConfirm : confirmUpload,
            onCancel: resolveProperties.open ? resolveProperties.onCancel : cancelUpload,
          };
        })()}
      />
      <FileInputs
        fileInputProps={fileInputProps}
        fileInputReference={fileInputReference}
        folderInputReference={folderInputReference}
      />
      <UploadDrawer files={files} uploadProgress={uploadProgress} isOpen={drawerOpen} onClose={handleDrawerClose} />
    </>
  );
};
